import moment from 'moment';

export type DateInput = Date | string | null | undefined;

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
const DEFAULT_DATE_TIME_FORMAT = 'MM/DD/YYYY h:mm a';

/**
 * Coerces a value into a Date, or null when absent/invalid. A date-only string
 * (e.g. '2026-04-17') is parsed as LOCAL midnight, not UTC, so the rendered day
 * never shifts in negative-offset timezones. This is the single coercion point;
 * do not use `new Date(str)` for display.
 * @registry Canonical string/Date-or-null coercion; use instead of `new Date(str)` anywhere a date is displayed.
 */
export const parseDate = (
  value: string | Date | null | undefined,
): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const m = moment(value);
  return m.isValid() ? m.toDate() : null;
};

/** Formats a date as 'MM/DD/YYYY' by default. Returns '' for absent/invalid input. @registry Canonical date-only display formatter; use instead of moment/toLocaleDateString in JSX. */
export const formatDate = (
  value: DateInput,
  format: string = DEFAULT_DATE_FORMAT,
): string => {
  if (value === null || value === undefined || value === '') return '';
  const m = moment(value);
  return m.isValid() ? m.format(format) : '';
};

/** Formats a date+time as 'MM/DD/YYYY h:mm a' by default. Returns '' for absent/invalid input. @registry Canonical date+time display formatter; use instead of moment/toLocaleString in JSX. */
export const formatDateTime = (
  value: DateInput,
  format: string = DEFAULT_DATE_TIME_FORMAT,
): string => {
  if (value === null || value === undefined || value === '') return '';
  const m = moment(value);
  return m.isValid() ? m.format(format) : '';
};

/**
 * Calendar-day key ('YYYY-MM-DD') for a value the API returns as a UTC-midnight
 * instant (e.g. '2026-09-18T00:00:00Z'). The day must be read in UTC: reading it
 * locally moves it to the previous day in negative-offset timezones.
 * @registry Canonical bucket key for API date-only fields; use instead of moment(value).format('YYYY-MM-DD').
 */
export const toUtcDateKey = (value: DateInput): string => {
  if (value === null || value === undefined || value === '') return '';
  const m = moment.utc(value);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

/** @registry The browser's IANA time zone id (e.g. 'America/Chicago'); send it to endpoints that print times server-side, such as report PDFs. */
export const getBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;
